import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { WidgetTypeEnum } from 'app/core/enums/generated/WidgetTypeEnum';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { BA_Style } from 'app/shared/fa-business-area-icons/fa-business-area-icons.service';
import { UserWidgetVm } from 'app/shared/generated/Administration/Models/Users/UserWidgetVm';

interface WidgetImage {
    src: string;
    alt: string;
}

/** The widgets a user can pick from in one business area, as one entry in the side nav. */
interface WidgetGroup {
    businessArea: BusinessAreaEnum;
    name: string;
    sectionId: string;
    widgets: UserWidgetVm[];
    onDashboardCount: number;
}

@Component({
    selector: 'pcg-user-widgets',
    imports: [FaIconComponent, NgbTooltip, NgOptimizedImage],
    templateUrl: './user-widgets.component.html',
    styleUrl: './user-widgets.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserWidgetsComponent implements OnInit {

    route = inject(ActivatedRoute);
    http = inject(HttpClient);
    ns = inject(NotificationService);

    BA_Style = BA_Style;
    faLock = faLock;

    userId!: number;
    widgets = signal<UserWidgetVm[]>([]);
    isSysInvUser = signal(false);
    isPharmacyUser = signal(false);
    isLoading = signal(true);
    selectedArea = signal<BusinessAreaEnum | null>(null);

    /**
     * The widgets the server returned, grouped by the business area (module) each belongs to and ordered by area
     * name. Mod-access filtering happens server-side in GetUserWidgetsAsync; this only arranges what came back.
     */
    groups = computed<WidgetGroup[]>(() => {
        const byArea = new Map<BusinessAreaEnum, UserWidgetVm[]>();
        for (const widget of this.widgets()) {
            const area: BusinessAreaEnum = widget.module ?? BusinessAreaEnum.Unassigned;
            byArea.set(area, [...(byArea.get(area) ?? []), widget]);
        }

        return [...byArea.entries()]
            .map(([businessArea, widgets]) => {
                const name = BusinessAreaEnum.toDescription(businessArea) ?? 'Other';
                return {
                    businessArea,
                    name,
                    sectionId: `widget-area-${name.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`,
                    widgets: [...widgets].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
                    onDashboardCount: widgets.filter(o => o.isOnDashboard).length
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    });

    /** The area being shown; falls back to the first area until the user picks one. */
    selectedGroup = computed<WidgetGroup | null>(() =>
        this.groups().find(o => o.businessArea === this.selectedArea())
            ?? this.groups()[0]
            ?? null
    );

    widgetImages = new Map<WidgetTypeEnum, WidgetImage>([
        [WidgetTypeEnum.Notifications, { src: 'assets/images/widgets/Notifications.png', alt: 'Notifications Widget' }],
        [WidgetTypeEnum.Messages, { src: 'assets/images/widgets/messages.png', alt: 'Messages Widget' }],
        [WidgetTypeEnum.FdaRecalls, { src: 'assets/images/widgets/fda-recalls.png', alt: 'FDA Recalls Widget' }],
        [WidgetTypeEnum.RxRefillRequest, { src: 'assets/images/widgets/refill-request.png', alt: 'Rx Refill Request Widget' }],
        [WidgetTypeEnum.SysInvShortcuts, { src: 'assets/images/widgets/sys-inv-shortcuts.png', alt: 'System Inventory Shortcuts Widget' }],
        [WidgetTypeEnum.RxSearch, { src: 'assets/images/widgets/rx-search.png', alt: 'Rx Search Widget' }],
        [WidgetTypeEnum.SupportTicket, { src: 'assets/images/widgets/SupportTicketWidget.png', alt: 'Support Ticket' }],
        [WidgetTypeEnum.OrganizationQuickNav, { src: 'assets/images/widgets/org-quick-nav.png', alt: 'Organization Quick Nav' }],
        [WidgetTypeEnum.SysInvCycleCounts, { src: 'assets/images/widgets/sys-inv-cycle-counts.png', alt: 'System Inventory Cycle Counts' }],
        [WidgetTypeEnum.ExpiringMedications, { src: 'assets/images/widgets/expiring-meds-widget.png', alt: 'Expiring Medications' }],
        [WidgetTypeEnum.NDCLookup, { src: 'assets/images/widgets/NDCLookup.png', alt: 'NDC Lookup' }],
        [WidgetTypeEnum.InsulinRequiresReview, { src: 'assets/images/widgets/insulin_requiring_review.png', alt: 'Insulin Requires Review' }],
        [WidgetTypeEnum.InsulinAwaitingRx, { src: 'assets/images/widgets/insulin_awaiting_rx.png', alt: 'Insulin Awaiting Rx Association' }],
        [WidgetTypeEnum.InsulinQueueSummary, { src: 'assets/images/widgets/insulin_queue_summary.png', alt: 'Insulin Queue Summary' }],
        [WidgetTypeEnum.IssuanceQuickScan, { src: 'assets/images/widgets/issuance_quick_nav.png', alt: 'Issuance Quick Scan' }]
    ]);

    /** The preview image for a widget type, looked up by type rather than array position. */
    imageFor(widgetType: WidgetTypeEnum): WidgetImage | undefined {
        return this.widgetImages.get(widgetType);
    }

    ngOnInit() {
        this.userId = Number(this.route.snapshot.params.id);
        this.getUserWidgets();
    }

    getUserWidgets() {
        this.http.get<SystemMessage>(`api/Admin/Widgets/GetUserWidgets?userId=${this.userId}`).subscribe((msg: SystemMessage) => {
            // The service can append a null when a required widget's row is missing; drop it rather than render it.
            this.widgets.set(((msg.model ?? []) as (UserWidgetVm | null)[]).filter((o): o is UserWidgetVm => o != null));
            this.isSysInvUser.set(msg?.value?.isSysInvUser ?? false);
            this.isPharmacyUser.set(msg?.value?.isPharmacyUser ?? false);
            this.isLoading.set(false);
        });
    }

    selectArea(group: WidgetGroup) {
        this.selectedArea.set(group.businessArea);
    }

    /**
     * Whether the widget is on the dashboard and cannot be taken off it: either flagged IsRequired (ToggleWidgetAsync
     * refuses to remove those) or forced on for this user's role (Sys Inv / Pharmacy User rules below).
     */
    isLocked(widget: UserWidgetVm): boolean {
        return widget.isOnDashboard
            && (
                widget.isRequired
                || (this.isSysInvUser() && widget.widgetType == WidgetTypeEnum.SysInvShortcuts)
                || (this.isPharmacyUser() && widget.widgetType == WidgetTypeEnum.RxRefillRequest)
            );
    }

    /** Hover text for a required widget; null for an ordinary one so no tooltip shows. */
    requiredTooltip(widget: UserWidgetVm): string | null {
        if (this.isLocked(widget)) { return "This widget is required and cannot be removed."; }
        if (widget.isRequired) { return "This widget is required. Once added, it cannot be removed."; }
        return null;
    }

    toggleWidget(widget: UserWidgetVm) {
        if (
            this.isSysInvUser()
            && widget.widgetType == WidgetTypeEnum.SysInvShortcuts
        ) {
            this.ns.showError("System Inventory users cannot remove the System Inventory Shortcuts widget.");
            return;
        }
        if (
            this.isPharmacyUser()
            && widget.widgetType == WidgetTypeEnum.RxRefillRequest
        ) {
            this.ns.showError("Pharmacy users cannot remove the Rx Refill Request widget.");
            return;
        }
        if (this.isLocked(widget)) {
            this.ns.showError("This widget is required and cannot be removed.");
            return;
        }

        this.http.post<SystemMessage>('api/Admin/Widgets/ToggleWidget', widget).subscribe((msg: SystemMessage) => {
            if (msg.isSuccessful) {
                this.ns.showSuccess(msg.message!);
                this.widgets.update(list => list.map(o =>
                    o.widgetType == msg.value.widgetType
                        ? { ...o, isOnDashboard: msg.value.isAddedToDashboard }
                        : o
                ));
            } else {
                this.ns.showError(msg.message!);
            }
        });
    }
}
